// Traffic light rating — progress against targets (AoW level)
// Thresholds per Feb 11 agreement: green ≥80%, yellow 50–75%, red <50%

import { parseTocMappingEntries } from './dataProcessor/parsers.js';

export const THRESHOLDS = {
  green: 80,
  yellow: 50,
  // below 50% = red (3-tier system per agreement)
};

export const RATING_LABELS = {
  green: 'On track',
  yellow: 'Moderate progress',
  red: 'Behind',
  unknown: 'No data',
};

export const RATING_COLORS = {
  green: { dot: 'bg-green-500', bg: 'bg-green-50 border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700 border-green-200', rowBg: 'bg-green-50' },
  yellow: { dot: 'bg-yellow-500', bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700 border-yellow-200', rowBg: 'bg-yellow-50' },
  red: { dot: 'bg-red-500', bg: 'bg-red-50 border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700 border-red-200', rowBg: 'bg-red-50' },
  unknown: { dot: 'bg-gray-400', bg: 'bg-gray-50 border-gray-200', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-700 border-gray-200', rowBg: 'bg-gray-50' },
};

export function calculateAchievement(target, achieved) {
  if (!target || target === 0) return achieved > 0 ? 100 : null;
  return (achieved / target) * 100;
}

export function deriveRating(achievementPct) {
  if (achievementPct === null || achievementPct === undefined) return 'unknown';
  if (achievementPct >= THRESHOLDS.green) return 'green';
  if (achievementPct >= THRESHOLDS.yellow) return 'yellow';
  return 'red';
}

// Standard indicator categories (only these are used for automated roll-up;
// custom program-specific indicators are excluded per Feb 11 agreement)
export const STANDARD_INDICATOR_TYPES = [
  'Knowledge product',
  'Innovation development',
  'Innovation use',
  'Innovation Package',
  'Capacity sharing for development',
  'Policy change',
  'Other outcome',
  'Capacity change',
  'Other output',
  'Impact contribution',
];

// Map TOC indicator type (lowercased) → result type for narrowing matches.
// Covers ~82% of indicators; unmapped types fall back to broad matching.
const TOC_TYPE_TO_RESULT_TYPE = {
  'number of knowledge products': 'Knowledge product',
  'number of innovations (innovation development)': 'Innovation development',
  'number of people trained (capacity sharing for development)': 'Capacity sharing for development',
  'innovation use': 'Innovation use',
  'other output': 'Other output',
  'number of policy (policy change)': 'Policy change',
  'other outcome': 'Other outcome',
  'innovation package': 'Innovation Package',
  'capacity change': 'Capacity change',
  'impact contribution': 'Impact contribution',
};

function getResultTypeForIndicator(indicator) {
  if (!indicator.type) return null;
  return TOC_TYPE_TO_RESULT_TYPE[indicator.type.toLowerCase().trim()] || null;
}

// ---------------------------------------------------------------------------
// Indicator-used parsing helpers
// ---------------------------------------------------------------------------

function parseIndicatorLines(indicatorUsed) {
  if (!indicatorUsed) return [];
  const text = String(indicatorUsed).replace(/\u202f/g, ' ');
  return text.split(/\r?\n/)
    .map(line => line.trim())
    // Strip V24 "Primary: CODE - " or "Secondary: CODE - " prefixes
    .map(line => line.replace(/^(?:Primary|Secondary):\s*(?:SP\d{2}|INIT-\d+|PLAT-?\d*|SGP-?\d*)\s*-\s*/, ''))
    .map(line => line.replace(/\.\s*$/, '').replace(/\s+/g, ' ').trim())
    .filter(line => line && !/^(n\/a|not applicable|na)$/i.test(line));
}

function parseActualAchievedLines(actualAchieved) {
  if (actualAchieved == null) return [];
  const text = String(actualAchieved);
  return text.split(/\r?\n/).map(line => {
    let cleaned = line.trim();
    // Strip V24 "Primary: CODE - " or "Secondary: CODE - " prefixes
    cleaned = cleaned.replace(/^(?:Primary|Secondary):\s*(?:SP\d{2}|INIT-\d+|PLAT-?\d*|SGP-?\d*)\s*-\s*/, '');
    cleaned = cleaned.trim();
    if (!cleaned || /^(n\/a|not applicable|na)$/i.test(cleaned)) return null;
    const val = parseFloat(cleaned);
    if (isNaN(val)) return null;
    return Math.max(val, 0); // Clamp negatives
  });
}

/**
 * Lookup achieved value by program code from V24 multi-line actualAchieved.
 * Handles the case where indicatorUsed and actualAchieved have mismatched line
 * counts (e.g., SP06 omits its achieved value → positional indexing fails).
 */
function lookupAchievedByProgram(actualAchieved, programCode) {
  if (actualAchieved == null || !programCode) return null;
  const prefixRe = /^(?:Primary|Secondary):\s*(SP\d{2}|INIT-\d+|PLAT-?\d*|SGP-?\d*)\s*-\s*/;
  for (const line of String(actualAchieved).split(/\r?\n/)) {
    const m = line.trim().match(prefixRe);
    if (m && m[1] === programCode) {
      const cleaned = line.trim().replace(prefixRe, '').trim();
      if (!cleaned || /^(n\/a|not applicable|na)$/i.test(cleaned)) return null;
      const val = parseFloat(cleaned);
      return isNaN(val) ? null : Math.max(val, 0);
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tier detection helpers
// ---------------------------------------------------------------------------

/**
 * Check whether results have pre-joined indicatorUsedHits from the pipeline.
 */
function hasPipelineIndicatorUsedHits(results) {
  for (let i = 0; i < results.length; i++) {
    if (results[i].indicatorUsedHits &&
        (results[i].indicatorUsed || results[i].primaryIndicators?.length > 0)) return true;
  }
  return false;
}

/**
 * Check whether results have pre-joined tocHits from the pipeline.
 */
function hasPipelineTocHits(results) {
  for (let i = 0; i < results.length; i++) {
    if (results[i].tocMapping && results[i].tocHits) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Tier 1: Indicator-used matching
// ---------------------------------------------------------------------------

/**
 * Pipeline-accelerated: O(1) lookup in pre-computed indicatorUsedHits.
 * Returns array of { lineIndex, actualAchieved } for matching hits, or [].
 */
function findMatchViaIndicatorUsedHits(result, programCode, aowName, indicatorGlobalIndex) {
  const hits = result.indicatorUsedHits?.[programCode]?.[aowName];
  if (!hits) return [];
  return hits.filter(h => h.indicatorIndex === indicatorGlobalIndex);
}

/**
 * Runtime fallback: split indicatorUsed lines, match against tocIndicatorText.
 * Returns { matched: true, achievedValue, lineIndex } or null.
 */
function matchViaIndicatorUsed(result, tocIndicatorText, programCode) {
  if (!result.indicatorUsed || !tocIndicatorText) return null;

  const lines = parseIndicatorLines(result.indicatorUsed);
  if (lines.length === 0) return null;

  const aaLines = parseActualAchievedLines(result.actualAchieved);
  const tocNorm = tocIndicatorText.trim().replace(/\.\s*$/, '').replace(/\s+/g, ' ').replace(/\u202f/g, ' ').toLowerCase();

  for (let i = 0; i < lines.length; i++) {
    const lineNorm = lines[i].toLowerCase();
    // Exact match only (substring fallback disabled: || lineNorm.includes(tocNorm) || tocNorm.includes(lineNorm))
    if (lineNorm === tocNorm) {
      // Primary: positional (indicatorUsed and actualAchieved are parallel
      // multi-line fields — line N of each corresponds to the same indicator)
      let achievedValue = null;
      if (i < aaLines.length && aaLines[i] != null) {
        achievedValue = aaLines[i];
      }
      // Fallback: program code lookup (handles mismatched line counts where
      // one program omits its achieved value, shifting positions)
      if (achievedValue == null && programCode) {
        achievedValue = lookupAchievedByProgram(result.actualAchieved, programCode);
      }
      return { matched: true, achievedValue, lineIndex: i };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Tier 2: Pipeline tocHits (existing)
// ---------------------------------------------------------------------------

function findMatchingResultsViaHits(standardResults, programCode, aowName, indicatorGlobalIndex) {
  return standardResults.filter(r => {
    const hits = r.tocHits?.[programCode]?.[aowName];
    return hits && hits.includes(indicatorGlobalIndex);
  });
}

function countAowResultsViaHits(standardResults, programCode, aowName) {
  return standardResults.filter(r => {
    const hits = r.tocHits?.[programCode]?.[aowName];
    return hits && hits.length > 0;
  }).length;
}

function collectAowResultIdsViaHits(standardResults, programCode, aowName) {
  const ids = new Set();
  for (const r of standardResults) {
    const hits = r.tocHits?.[programCode]?.[aowName];
    if (hits && hits.length > 0) {
      ids.add(r.resultCode || `${r.title}::${r.type}::${r.initiative}`);
    }
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Tier 3: Legacy substring matching
// ---------------------------------------------------------------------------

function findMatchingResultsViaSubstring(standardResults, aowName, indicator) {
  const aowLower = aowName.toLowerCase();
  return standardResults.filter(r =>
    r.tocMapping && (
      (indicator.shortTitle && r.tocMapping.toLowerCase().includes(indicator.shortTitle.toLowerCase())) ||
      (indicator.indicator && r.tocMapping.toLowerCase().includes(indicator.indicator.toLowerCase())) ||
      r.tocMapping.toLowerCase().includes(aowLower)
    )
  );
}

// ---------------------------------------------------------------------------
// Structural ToC Mapping helpers
// ---------------------------------------------------------------------------

function normalizeTitle(s) {
  return (s || '').toLowerCase().replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').replace(/:+\s*$/, '').trim();
}

/**
 * Build a Map<normalizedShortTitle, aowName> from the ToC indicators.
 * Bridges tocMapping entries (which reference shortTitles like "2.3.3 Recover")
 * to ToC AoWs (like "AOW02").
 */
function buildShortTitleToAowMap(tocDataForProgram) {
  const map = new Map();
  for (const ind of tocDataForProgram.indicators) {
    if (ind.shortTitle && ind.aow) {
      map.set(normalizeTitle(ind.shortTitle), ind.aow);
    }
  }
  return map;
}

/**
 * Get parsed tocMapping entries for a result, with caching.
 */
function getParsedTocMapping(result) {
  if (result._tocMappingParsed !== undefined) return result._tocMappingParsed;
  result._tocMappingParsed = parseTocMappingEntries(result.tocMapping);
  return result._tocMappingParsed;
}

/**
 * Check whether a tocMapping entry's shortTitle matches an indicator's shortTitle.
 * Uses normalized comparison with substring fallback for the ~3% that don't exact-match.
 */
function shortTitleMatches(entryShortTitle, indicatorShortTitle) {
  if (!entryShortTitle || !indicatorShortTitle) return false;
  const a = normalizeTitle(entryShortTitle);
  const b = normalizeTitle(indicatorShortTitle);
  if (a === b) return true;
  // Substring fallback disabled — only exact normalized match
  // if (a.length > 5 && b.length > 5) {
  //   return a.includes(b) || b.includes(a);
  // }
  return false;
}

/**
 * Count how many standard results map to a specific AoW via structural tocMapping parsing.
 */
function countAowResultsStructural(standardResults, programCode, aowName, shortTitleToAow) {
  return standardResults.filter(r => {
    const entries = getParsedTocMapping(r);
    return entries.some(e => {
      if (e.program !== programCode) return false;
      const norm = normalizeTitle(e.shortTitle);
      return shortTitleToAow.get(norm) === aowName;
    });
  }).length;
}

function collectAowResultIdsStructural(standardResults, programCode, aowName, shortTitleToAow) {
  const ids = new Set();
  for (const r of standardResults) {
    const entries = getParsedTocMapping(r);
    if (entries.some(e => e.program === programCode && shortTitleToAow.get(normalizeTitle(e.shortTitle)) === aowName)) {
      ids.add(r.resultCode || `${r.title}::${r.type}::${r.initiative}`);
    }
  }
  return ids;
}

// ---------------------------------------------------------------------------
// HLO grouping (sum-based achieved)
// ---------------------------------------------------------------------------

/**
 * Group flat indicator rows into HLOs by shortTitle.
 * Each HLO aggregates target/achieved from its child indicators.
 * Uses sum of achieved values (not count of unique result IDs).
 */
function groupIndicatorsIntoHLOs(indicatorResults) {
  const groupMap = new Map();
  for (const ind of indicatorResults) {
    const key = ind.shortTitle || ind.indicator || 'Ungrouped';
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        shortTitle: ind.shortTitle || '',
        resultType: ind.resultType || '',
        indicators: [],
      });
    }
    groupMap.get(key).indicators.push(ind);
  }

  return Array.from(groupMap.values()).map(group => {
    const children = group.indicators;
    const target = children.reduce((sum, ind) => sum + (ind.target || 0), 0);
    // Sum achieved values across child indicators (already deduped at indicator level)
    // Exclude unaligned results (no indicator info) from HLO achievement calculation
    const achieved = children.reduce((sum, ind) => sum + (ind.isUnaligned ? 0 : (ind.achieved || 0)), 0);
    const achievedW1W2 = children.reduce((sum, ind) => sum + (ind.isUnaligned ? 0 : (ind.achievedW1W2 || 0)), 0);
    const achievedW3 = children.reduce((sum, ind) => sum + (ind.isUnaligned ? 0 : (ind.achievedW3 || 0)), 0);
    // Capped percentage average (matches AoW-level logic): cap each indicator at
    // 100% before averaging so overachievers can't compensate for underperformers
    const aligned = children.filter(ind => !ind.isUnaligned);
    const withPct = aligned.filter(ind => ind.achievementPct !== null);
    const achievementPct = withPct.length > 0
      ? withPct.reduce((sum, ind) => sum + Math.min(ind.achievementPct, 100), 0) / withPct.length
      : null;

    // Carry year-level targets for trajectory chart compatibility
    const yearTargets = {};
    for (const y of [2025, 2026, 2027, 2028, 2029, 2030]) {
      yearTargets[`target${y}`] = children.reduce(
        (sum, ind) => sum + (parseFloat(ind[`target${y}`]) || 0), 0
      );
    }

    return {
      shortTitle: group.shortTitle,
      resultType: group.resultType,
      indicators: children,
      target, achieved, achievedW1W2, achievedW3, achievementPct,
      rating: deriveRating(achievementPct),
      ...yearTargets,
    };
  });
}

// ---------------------------------------------------------------------------
// Main calculation
// ---------------------------------------------------------------------------

/**
 * Check whether results have V22 structured indicator fields (primaryIndicators/contributorIndicators).
 */
function hasV22Indicators(results) {
  for (let i = 0; i < results.length; i++) {
    if (results[i].primaryIndicators?.length > 0 || results[i].contributorIndicators?.length > 0) return true;
  }
  return false;
}

/**
 * Normalize text for indicator comparison: lowercase, collapse whitespace,
 * strip trailing punctuation.
 */
function normalizeIndicatorText(s) {
  return (s || '').toLowerCase().replace(/\s+/g, ' ').replace(/[.\s]+$/, '').trim();
}

/**
 * Tier 0: V22 explicit primary/contributor indicator matching.
 * Returns { matched: true, achievedValue } or null.
 *
 * Checks primaryIndicators and contributorIndicators on the result for entries
 * that reference the given programCode AND whose text matches the ToC indicator text.
 */
function matchViaV22Indicators(result, programCode, tocIndicatorText, indicatorShortTitle) {
  const tocNorm = normalizeIndicatorText(tocIndicatorText);
  if (!tocNorm) return null;

  // Verify the result's tocMapping confirms this specific HLO/shortTitle.
  // Prevents matching duplicate indicator text across unrelated HLOs.
  const confirmShortTitle = () => {
    if (!indicatorShortTitle) return true;
    const entries = getParsedTocMapping(result);
    return entries.some(e =>
      e.program === programCode && shortTitleMatches(e.shortTitle, indicatorShortTitle)
    );
  };

  // Check primaryIndicators
  if (result.primaryIndicators) {
    for (let idx = 0; idx < result.primaryIndicators.length; idx++) {
      const pi = result.primaryIndicators[idx];
      if (pi.program !== programCode) continue;
      if (normalizeIndicatorText(pi.text) === tocNorm && confirmShortTitle()) {
        // Primary: positional (primaryIndicators and primaryAchieved are parallel arrays)
        let achievedValue = null;
        if (idx < (result.primaryAchieved?.length || 0)) {
          achievedValue = result.primaryAchieved[idx].value;
        }
        // Fallback: program code lookup (for mismatched array lengths)
        if (achievedValue == null) {
          const achievedEntry = result.primaryAchieved?.find(a => a.program === pi.program);
          achievedValue = achievedEntry?.value ?? null;
        }
        return { matched: true, achievedValue };
      }
    }
  }

  // Check contributorIndicators
  if (result.contributorIndicators) {
    for (let idx = 0; idx < result.contributorIndicators.length; idx++) {
      const ci = result.contributorIndicators[idx];
      if (ci.program !== programCode) continue;
      if (normalizeIndicatorText(ci.text) === tocNorm && confirmShortTitle()) {
        // Primary: positional (contributorIndicators and contributorAchieved are parallel arrays)
        let achievedValue = null;
        if (idx < (result.contributorAchieved?.length || 0)) {
          achievedValue = result.contributorAchieved[idx].value;
        }
        // Fallback: program code lookup (for mismatched array lengths)
        if (achievedValue == null) {
          const achievedEntry = result.contributorAchieved?.find(a => a.program === ci.program);
          achievedValue = achievedEntry?.value ?? null;
        }
        return { matched: true, achievedValue };
      }
    }
  }

  return null;
}

/**
 * Calculate traffic light ratings per Area of Work from TOC data and results.
 * Uses a multi-tier matching cascade with value enhancement:
 *   Tier 0: V22 explicit primary/contributor indicator fields (program-scoped, HLO-aware)
 *   Tier A: pipeline tocHits + indicatorUsedHits (pre-computed, HLO-aware)
 *   Tier B: structural tocMapping (shortTitle provides HLO constraint)
 *   Enhancement: after match, extract actualAchieved from indicatorUsed fields
 * indicatorUsed is NOT used as an independent match criterion to avoid
 * overcounting (e.g., every KP matching every KP indicator across all HLOs).
 */
export function calculateTrafficLights(tocDataForProgram, filteredResults, year = '2025', allResults = null, resultScope = 'all') {
  if (!tocDataForProgram?.indicators || !tocDataForProgram?.areasOfWork) {
    return { aows: [], unmatchedMeta: null };
  }

  const programCode = tocDataForProgram.programCode;
  const targetField = `target${year}`;

  // Build shortTitle→AoW lookup for structural matching
  const shortTitleToAow = buildShortTitleToAowMap(tocDataForProgram);

  // Build results pool: program-filtered results + cross-program contributions
  // Cross-program results are those from OTHER programs that reference THIS program
  // in their tocMapping field (e.g., SP10 result mapped to SP08's ToC),
  // or via V22 contributorIndicators.
  const resultsPool = allResults || filteredResults;
  const standardResults = resultsPool.filter(r => {
    if (!STANDARD_INDICATOR_TYPES.includes(r.type)) return false;

    const isSubmitted = r.initiative === programCode;
    const isContributed = !isSubmitted && (
      (r.contributorIndicators?.some(ci => ci.program === programCode)) ||
      getParsedTocMapping(r).some(e => e.program === programCode)
    );

    if (resultScope === 'submitted') return isSubmitted;
    if (resultScope === 'contributed') return isContributed;
    return isSubmitted || isContributed; // 'all' or fallback
  });

  // Detect V22/pipeline fields using program-filtered results (not all results,
  // which may start with legacy INIT-* rows that lack V22 fields)
  const useV22 = hasV22Indicators(standardResults);
  const useIndicatorUsedHits = hasPipelineIndicatorUsedHits(standardResults);
  const usePipelineHits = hasPipelineTocHits(standardResults);

  // Build a global index for each indicator
  const indicatorGlobalIndices = new Map();
  tocDataForProgram.indicators.forEach((ind, i) => {
    indicatorGlobalIndices.set(ind, i);
  });

  const aowsResult = tocDataForProgram.areasOfWork.map(aowName => {
    const aowIndicators = tocDataForProgram.indicators.filter(
      ind => ind.aow === aowName && ind[targetField] != null
    );

    // Build funding type lookup for splitting achieved by W1W2 vs W3Bilateral
    const resultFundingLookup = new Map();
    for (const result of standardResults) {
      const rid = result.resultCode || `${result.title}::${result.type}::${result.initiative}`;
      resultFundingLookup.set(rid, result.fundingType || 'W1W2');
    }

    if (aowIndicators.length === 0) {
      let resultCount;
      if (usePipelineHits) {
        resultCount = countAowResultsViaHits(standardResults, programCode, aowName);
      } else {
        resultCount = countAowResultsStructural(standardResults, programCode, aowName, shortTitleToAow);
      }
      return {
        name: aowName,
        rating: 'unknown',
        progress: 0,
        achievementPct: null,
        indicators: [],
        hlos: [],
        resultCount,
        achievedW1W2: 0,
        achievedW3: 0,
      };
    }

    const indicatorResults = aowIndicators.map(indicator => {
      const mappedResultType = getResultTypeForIndicator(indicator);
      const globalIdx = indicatorGlobalIndices.get(indicator);
      const tocIndicatorText = indicator.indicator || '';
      const target = parseFloat(indicator[targetField]) || 0;

      let totalAchieved = 0;
      let totalAchievedW1W2 = 0;
      let totalAchievedW3 = 0;
      const matchedResultIds = new Map();

      for (const result of standardResults) {
        const resultId = result.resultCode || `${result.title}::${result.type}::${result.initiative}`;
        if (matchedResultIds.has(resultId)) continue; // Already counted

        let matched = false;
        const defaultValue = result.type === 'Knowledge product' ? 1 : 0; // KPs count as 1; others need a reported value
        let achievedValue = defaultValue;
        let tier0Matched = false;

        // Tier 0: V22 explicit primary/contributor indicator matching
        // Runs BEFORE type narrowing — explicit declarations take precedence
        if (useV22) {
          const v22Match = matchViaV22Indicators(result, programCode, tocIndicatorText, indicator.shortTitle);
          if (v22Match) {
            matched = true;
            tier0Matched = true;
            achievedValue = v22Match.achievedValue ?? defaultValue;
          } else if (
            result.primaryIndicators?.some(pi => pi.program === programCode) ||
            result.contributorIndicators?.some(ci => ci.program === programCode)
          ) {
            // V22 explicitly maps this result to a different indicator — skip lower tiers
            continue;
          }
        }

        // Type narrowing disabled — V22 Tier 0 explicit declarations can map
        // results of any type to any indicator (e.g. "Other outcome" → "Policy change")
        // if (mappedResultType && result.type !== mappedResultType) continue;

        // Results without any indicator information (no V22 for this program,
        // no indicatorUsed text) can't be assigned to a specific indicator.
        // They're collected into "not aligned" buckets per HLO afterward.
        if (!matched && !result.indicatorUsed) {
          continue;
        }

        // Tier A: pipeline tocHits (pre-computed, HLO-aware)
        if (!matched && usePipelineHits && globalIdx !== undefined) {
          const hits = result.tocHits?.[programCode]?.[aowName];
          if (hits && hits.includes(globalIdx)) {
            matched = true;
          }
        }

        // Also check pipeline indicatorUsedHits (pre-computed, HLO-aware)
        if (!matched && useIndicatorUsedHits && globalIdx !== undefined) {
          const hits = findMatchViaIndicatorUsedHits(result, programCode, aowName, globalIdx);
          if (hits.length > 0) {
            matched = true;
            achievedValue = hits[0].actualAchieved != null ? hits[0].actualAchieved : defaultValue;
          }
        }

        // Tier B: structural tocMapping match (shortTitle provides HLO constraint)
        if (!matched && result.tocMapping) {
          const entries = getParsedTocMapping(result);
          for (const entry of entries) {
            if (entry.program !== programCode) continue;
            if (shortTitleMatches(entry.shortTitle, indicator.shortTitle)) {
              matched = true;
              break;
            }
          }
        }

        // Value enhancement: if matched via structure/pipeline (not Tier 0), try to extract
        // actualAchieved from indicatorUsed fields (better than counting as 1)
        if (matched && !tier0Matched && achievedValue === defaultValue && result.indicatorUsed) {
          const indMatch = matchViaIndicatorUsed(result, tocIndicatorText, programCode);
          if (indMatch && indMatch.achievedValue != null) {
            achievedValue = indMatch.achievedValue;
          }
        }

        // Direct fallback: use result.actualAchieved when indicatorUsed text
        // matching failed but the value is unambiguous
        if (matched && !tier0Matched && achievedValue === defaultValue && result.actualAchieved != null) {
          // Primary: program-code lookup (handles V24 mismatched line counts)
          const programValue = lookupAchievedByProgram(result.actualAchieved, programCode);
          if (programValue != null) {
            achievedValue = programValue;
          } else {
            // Fallback: positional, only when unambiguous (single value, no V24 prefixes)
            const aaLines = parseActualAchievedLines(result.actualAchieved);
            const validValues = aaLines.filter(v => v != null);
            if (validValues.length === 1) {
              achievedValue = validValues[0];
            }
          }
        }

        // V22 value enhancement: when matched via non-Tier-0 and result has V22
        // structured achieved values, use those instead of default 1
        if (matched && !tier0Matched && achievedValue === defaultValue && useV22) {
          if (result.primaryAchieved) {
            const entry = result.primaryAchieved.find(a => a.program === programCode);
            if (entry && entry.value != null) {
              achievedValue = entry.value;
            }
          }
          if (achievedValue === defaultValue && result.contributorAchieved) {
            const entry = result.contributorAchieved.find(a => a.program === programCode);
            if (entry && entry.value != null) {
              achievedValue = entry.value;
            }
          }
        }

        if (matched) {
          matchedResultIds.set(resultId, achievedValue);
          totalAchieved += achievedValue;
          if (result.fundingType === 'W3Bilateral') {
            totalAchievedW3 += achievedValue;
          } else {
            totalAchievedW1W2 += achievedValue;
          }
        }
      }

      const pct = calculateAchievement(target, totalAchieved);

      return {
        ...indicator,
        target,
        achieved: totalAchieved,
        achievedW1W2: totalAchievedW1W2,
        achievedW3: totalAchievedW3,
        matchedResultIds,
        achievementPct: pct,
        rating: deriveRating(pct),
      };
    });

    // Deduplicate results within each shortTitle group so per-indicator sums
    // don't double-count. Mapped indicators (type-narrowed) claim first; unmapped
    // indicators scale their achieved proportionally for unclaimed results.
    const shortTitleGroups = new Map();
    for (const ind of indicatorResults) {
      const key = ind.shortTitle || ind.indicator || 'Ungrouped';
      if (!shortTitleGroups.has(key)) shortTitleGroups.set(key, []);
      shortTitleGroups.get(key).push(ind);
    }
    for (const [, group] of shortTitleGroups) {
      if (group.length <= 1) continue;
      const claimed = new Set();
      const mapped = group.filter(ind => getResultTypeForIndicator(ind) !== null);
      const unmapped = group.filter(ind => getResultTypeForIndicator(ind) === null);
      for (const ind of mapped) {
        if (ind.matchedResultIds) {
          for (const [id] of ind.matchedResultIds) claimed.add(id);
        }
      }
      // Each unmapped indicator only gets results not yet claimed
      for (const ind of unmapped) {
        if (ind.matchedResultIds) {
          const originalCount = ind.matchedResultIds.size;
          const unclaimed = new Map([...ind.matchedResultIds].filter(([id]) => !claimed.has(id)));
          for (const [id] of unclaimed) claimed.add(id);
          // Recompute W1W2/W3 and total achieved from actual unclaimed values
          // (proportional scaling diverges when results have non-uniform achieved values)
          let recomputedW1W2 = 0, recomputedW3 = 0;
          for (const [id, value] of unclaimed) {
            if ((resultFundingLookup.get(id) || 'W1W2') === 'W3Bilateral') recomputedW3 += value;
            else recomputedW1W2 += value;
          }
          ind.achieved = recomputedW1W2 + recomputedW3;
          ind.achievedW1W2 = recomputedW1W2;
          ind.achievedW3 = recomputedW3;
          ind.matchedResultIds = unclaimed;
          ind.achievementPct = calculateAchievement(ind.target, ind.achieved);
          ind.rating = deriveRating(ind.achievementPct);
        }
      }
    }

    // Collect results mapped to this AoW's shortTitles via tocMapping but lacking
    // indicator info — bucket as "Results not aligned to ToC indicators" per HLO
    const allMatchedIds = new Set();
    for (const ind of indicatorResults) {
      if (ind.matchedResultIds) {
        for (const [id] of ind.matchedResultIds) allMatchedIds.add(id);
      }
    }
    const shortTitlesInAow = new Set(aowIndicators.map(i => i.shortTitle).filter(Boolean));
    const unalignedByShortTitle = new Map();
    for (const result of standardResults) {
      if (result.indicatorUsed) continue;
      if (result.primaryIndicators?.some(pi => pi.program === programCode)) continue;
      if (result.contributorIndicators?.some(ci => ci.program === programCode)) continue;
      const resultId = result.resultCode || `${result.title}::${result.type}::${result.initiative}`;
      if (allMatchedIds.has(resultId)) continue;
      const entries = getParsedTocMapping(result);
      for (const entry of entries) {
        if (entry.program !== programCode) continue;
        for (const st of shortTitlesInAow) {
          if (shortTitleMatches(entry.shortTitle, st)) {
            if (!unalignedByShortTitle.has(st)) unalignedByShortTitle.set(st, new Map());
            if (!unalignedByShortTitle.get(st).has(resultId)) {
              unalignedByShortTitle.get(st).set(resultId, 1);
            }
            break;
          }
        }
      }
    }
    for (const [st, resultIds] of unalignedByShortTitle) {
      if (resultIds.size === 0) continue;
      let unalignedW1W2 = 0, unalignedW3 = 0;
      for (const [id] of resultIds) {
        if ((resultFundingLookup.get(id) || 'W1W2') === 'W3Bilateral') unalignedW3++;
        else unalignedW1W2++;
      }
      indicatorResults.push({
        shortTitle: st,
        indicator: 'Results not aligned to ToC indicators',
        type: '',
        aow: aowName,
        target: 0,
        achieved: resultIds.size,
        achievedW1W2: unalignedW1W2,
        achievedW3: unalignedW3,
        matchedResultIds: resultIds,
        achievementPct: null,
        rating: 'unknown',
        displayName: 'Results not aligned to ToC indicators',
        isUnaligned: true,
      });
    }

    // Add display names using the specific indicator text when shortTitle is shared
    indicatorResults.forEach(ind => {
      const baseName = ind.shortTitle || ind.indicator || 'Indicator';
      const hasDuplicateShortTitle = indicatorResults.some(
        other => other !== ind &&
          (other.shortTitle || other.indicator) === (ind.shortTitle || ind.indicator)
      );
      if (!hasDuplicateShortTitle) {
        ind.displayName = baseName;
      } else if (ind.indicator && ind.indicator !== ind.shortTitle) {
        ind.displayName = ind.shortTitle ? `${ind.shortTitle} — ${ind.indicator}` : ind.indicator;
      } else if (ind.type) {
        ind.displayName = `${baseName} — ${ind.type}`;
      } else {
        ind.displayName = baseName;
      }
    });

    // Aggregate: average of indicator achievements within this AoW
    const validIndicators = indicatorResults.filter(i => i.achievementPct !== null);
    const avgAchievement = validIndicators.length > 0
      ? validIndicators.reduce((sum, i) => sum + Math.min(i.achievementPct, 100), 0) / validIndicators.length
      : null;

    let aowResultIdSet;
    if (usePipelineHits) {
      aowResultIdSet = collectAowResultIdsViaHits(standardResults, programCode, aowName);
    } else {
      aowResultIdSet = collectAowResultIdsStructural(standardResults, programCode, aowName, shortTitleToAow);
    }

    const hlos = groupIndicatorsIntoHLOs(indicatorResults);
    const aowAchievedW1W2 = hlos.reduce((sum, h) => sum + (h.achievedW1W2 || 0), 0);
    const aowAchievedW3 = hlos.reduce((sum, h) => sum + (h.achievedW3 || 0), 0);

    return {
      name: aowName,
      rating: deriveRating(avgAchievement),
      progress: Math.min(Math.round(avgAchievement || 0), 100),
      achievementPct: avgAchievement,
      indicators: indicatorResults,
      hlos,
      resultCount: aowResultIdSet.size,
      aowResultIds: aowResultIdSet,
      achievedW1W2: aowAchievedW1W2,
      achievedW3: aowAchievedW3,
    };
  });

  // ---------------------------------------------------------------------------
  // Compute unmatched results metadata
  // ---------------------------------------------------------------------------
  const allAowMatchedIds = new Set();
  for (const aow of aowsResult) {
    if (aow.aowResultIds) {
      for (const id of aow.aowResultIds) allAowMatchedIds.add(id);
    }
  }

  // Build result ID → result lookup from standardResults
  const standardResultsById = new Map();
  for (const r of standardResults) {
    const rid = r.resultCode || `${r.title}::${r.type}::${r.initiative}`;
    standardResultsById.set(rid, r);
  }

  // Results in pool but not matched to any AoW
  const unmatchedInPool = [];
  for (const [rid, r] of standardResultsById) {
    if (!allAowMatchedIds.has(rid)) {
      const entries = getParsedTocMapping(r);
      const hasTocMapping = entries.length > 0;
      unmatchedInPool.push({
        resultId: rid,
        reason: hasTocMapping ? 'toc-no-aow-match' : 'no-toc-mapping',
      });
    }
  }

  // Results in filteredResults but not in standardResults pool
  const notInPool = [];
  for (const r of filteredResults) {
    const rid = r.resultCode || `${r.title}::${r.type}::${r.initiative}`;
    if (!standardResultsById.has(rid)) {
      const isNonStandardType = !STANDARD_INDICATOR_TYPES.includes(r.type);
      notInPool.push({
        resultId: rid,
        reason: isNonStandardType ? 'non-standard-type' : 'not-in-toc-pool',
      });
    }
  }

  const unmatchedMeta = {
    totalSidebar: filteredResults.length,
    totalInPool: standardResults.length,
    totalMatchedToAow: allAowMatchedIds.size,
    unmatchedInPool,
    notInPool,
  };

  return { aows: aowsResult, unmatchedMeta };
}
